import type { CSSProperties } from 'react'

import type { FloatActionsPosition, FloatActionsSize } from '#/components/FloatActions/types'

const STORAGE_KEY = 'float-actions-position'

const VIEWPORT_MARGIN = 8

const DEFAULT_CORNER_DISTANCE = 16

/** Indica que o valor gravado tem as duas coordenadas numéricas */
function isFloatActionsPosition(value: unknown): value is FloatActionsPosition {
  const candidate = value as Partial<Record<keyof FloatActionsPosition, unknown>> | null

  return Number.isFinite(candidate?.x) && Number.isFinite(candidate?.y)
}

/** Limita a coordenada ao intervalo que mantém o grupo inteiro dentro da janela */
function clampCoordinate(coordinate: number, elementLength: number, viewportLength: number): number {
  const minimumCoordinate = elementLength + VIEWPORT_MARGIN

  return Math.max(minimumCoordinate, Math.min(coordinate, viewportLength - VIEWPORT_MARGIN))
}

/** Posição ajustada para que o grupo não ultrapasse as bordas da janela */
export function clampFloatActionsPosition(
  position: FloatActionsPosition,
  elementSize: FloatActionsSize,
  viewportSize: FloatActionsSize,
): FloatActionsPosition {
  return {
    x: clampCoordinate(position.x, elementSize.width, viewportSize.width),
    y: clampCoordinate(position.y, elementSize.height, viewportSize.height),
  }
}

/**
 * Estilo de posicionamento do grupo; ancorado pelo canto inferior direito para que botões
 * que entram ou saem não desloquem os demais
 */
export function floatActionsPositionStyle(position: FloatActionsPosition | null): CSSProperties {
  if (!position) {
    return { bottom: DEFAULT_CORNER_DISTANCE, right: DEFAULT_CORNER_DISTANCE }
  }

  return { left: position.x, top: position.y, transform: 'translate(-100%, -100%)' }
}

/** Lê a posição gravada no navegador; nulo enquanto o grupo nunca foi arrastado */
export function readFloatActionsPosition(): FloatActionsPosition | null {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? 'null')

    return isFloatActionsPosition(stored) ? stored : null
  } catch {
    return null
  }
}

/** Grava a posição no navegador; sem localStorage ela vale só até recarregar a página */
export function writeFloatActionsPosition(position: FloatActionsPosition): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(position))
  } catch {
    // O localStorage pode estar indisponível ou cheio
  }
}
